import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { 
  ArrowLeft, 
  ShieldCheck, 
  AlertTriangle, 
  Pill, 
  FileText,
  Droplets,
  Clock,
  AlertCircle
} from 'lucide-react';
import { mockProducts } from '../mock';

export const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = mockProducts.find(p => p.id === id);

  if (!product) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Product not found</h2>
          <Button onClick={() => navigate('/')} className="bg-blue-600 hover:bg-blue-700">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Products
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50">
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/')}
          className="mb-6 hover:bg-blue-50 text-blue-700"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Products
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Product Image */}
          <div className="relative">
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-50 to-gray-50 aspect-square">
              <img
                src={product.image}
                alt={product.name}
                className="h-full w-full object-cover"
              />
              <div className="absolute top-4 right-4 flex flex-col gap-2">
                <Badge 
                  variant={product.inStock ? "default" : "secondary"} 
                  className={`${product.inStock ? "bg-green-500" : "bg-gray-400"} text-white`}
                >
                  {product.inStock ? "In Stock" : "Out of Stock"}
                </Badge>
                {product.prescription && (
                  <Badge variant="destructive" className="bg-red-500">
                    Prescription Required
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Product Info */}
          <div>
            <Badge variant="outline" className="mb-3 text-blue-700 border-blue-300 bg-blue-50">
              {product.category}
            </Badge>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{product.name}</h1>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">{product.description}</p>

            <div className="flex items-center gap-2 mb-6">
              <ShieldCheck className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-700">Quality Assured & Certified</span>
            </div>

            <Separator className="my-6" />

            {/* Quick Info Cards */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="border-blue-100 bg-blue-50/50">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Pill className="h-5 w-5 text-blue-600" />
                    <span className="font-semibold text-gray-900">Type</span>
                  </div>
                  <p className="text-sm text-gray-600">{product.category}</p>
                </CardContent>
              </Card>

              <Card className="border-green-100 bg-green-50/50">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck className="h-5 w-5 text-green-600" />
                    <span className="font-semibold text-gray-900">Status</span>
                  </div>
                  <p className="text-sm text-gray-600">{product.inStock ? "Available" : "Out of Stock"}</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Detailed Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Composition */}
          <Card className="border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-transparent">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Droplets className="h-5 w-5 text-blue-600" />
                Composition
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-gray-700 leading-relaxed">{product.composition}</p>
            </CardContent>
          </Card>

          {/* Dosage */}
          <Card className="border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="bg-gradient-to-r from-green-50 to-transparent">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Clock className="h-5 w-5 text-green-600" />
                Dosage
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-gray-700 leading-relaxed">{product.dosage}</p>
            </CardContent>
          </Card>

          {/* Usage Instructions */}
          <Card className="border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-transparent">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <FileText className="h-5 w-5 text-purple-600" />
                Usage Instructions
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-gray-700 leading-relaxed">{product.usage}</p>
            </CardContent>
          </Card>

          {/* Precautions */}
          <Card className="border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="bg-gradient-to-r from-amber-50 to-transparent">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                Precautions
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-gray-700 leading-relaxed">{product.precautions}</p>
            </CardContent>
          </Card>

          {/* Side Effects */}
          <Card className="border-red-200 shadow-sm hover:shadow-md transition-shadow lg:col-span-2">
            <CardHeader className="bg-gradient-to-r from-red-50 to-transparent">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                Possible Side Effects
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-gray-700 leading-relaxed">{product.sideEffects}</p>
            </CardContent>
          </Card>
        </div>

        {/* Important Notice */}
        <Card className="mt-6 border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <ShieldCheck className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Important Medical Notice</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  This information is for reference only. Always consult with a qualified healthcare professional 
                  before starting, stopping, or changing any medication. Self-medication can be dangerous.
                  {product.prescription && " This product requires a valid prescription from a licensed physician."}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
};
